import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Typography, Avatar } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MicIcon from '@mui/icons-material/Mic';
import { avatarColor, getInitials } from './chatHelpers';
import { BAR_COUNT, buildWaveformHeights, formatAudioDuration } from './voiceWaveformUtils';

interface Props {
  src?: string;
  durationMs?: number;
  isOwn: boolean;
  senderName?: string;
  senderAvatar?: string;
  senderId?: string;
  seed?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export const CommunityVoiceNotePlayer: React.FC<Props> = ({
  src,
  durationMs = 0,
  isOwn,
  senderName = 'Member',
  senderAvatar,
  senderId,
  seed = 'audio',
  onClick,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [loadedDurationMs, setLoadedDurationMs] = useState(durationMs);

  const waveform = useMemo(() => buildWaveformHeights(seed, BAR_COUNT), [seed]);

  const colors = {
    playIcon: isOwn ? '#54656f' : '#fff',
    playBtnBg: isOwn ? 'rgba(255,255,255,0.95)' : '#25D366',
    progressDot: isOwn ? '#54656f' : '#25D366',
    trackLine: isOwn ? 'rgba(0,0,0,0.25)' : 'rgba(37,211,102,0.35)',
    barPlayed: isOwn ? '#54656f' : '#25D366',
    barUnplayed: isOwn ? 'rgba(0,0,0,0.2)' : 'rgba(37,211,102,0.28)',
    duration: isOwn ? 'rgba(0,0,0,0.55)' : '#667781',
  };

  useEffect(() => {
    setLoadedDurationMs(durationMs);
  }, [durationMs]);

  useEffect(() => () => audioRef.current?.pause(), []);

  const togglePlay = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!src || !audioRef.current) return;
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        void audioRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
    },
    [src, isPlaying]
  );

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio?.duration) return;
    setProgress(audio.currentTime / audio.duration);
    setElapsedMs(audio.currentTime * 1000);
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio?.duration || !Number.isFinite(audio.duration)) return;
    const ms = audio.duration * 1000;
    if (ms > 0) setLoadedDurationMs(ms);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setElapsedMs(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  const displayMs = isPlaying || elapsedMs > 0 ? elapsedMs : loadedDurationMs;
  const progressIndex = Math.floor(progress * BAR_COUNT);

  return (
    <Box
      onClick={onClick}
      sx={{ minWidth: 260, maxWidth: 340, cursor: onClick ? 'pointer' : 'default', py: 0.25 }}
    >
      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {!isOwn && (
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={senderAvatar}
              sx={{
                width: 34,
                height: 34,
                bgcolor: avatarColor(senderId ?? senderName),
                fontSize: '0.65rem',
              }}
            >
              {getInitials(senderName)}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                right: -3,
                bottom: -3,
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
              }}
            >
              <MicIcon sx={{ fontSize: 10, color: '#25D366' }} />
            </Box>
          </Box>
        )}

        <IconButton
          onClick={togglePlay}
          disabled={!src}
          size="small"
          sx={{
            width: 32,
            height: 32,
            bgcolor: colors.playBtnBg,
            color: colors.playIcon,
            flexShrink: 0,
            boxShadow: isOwn ? 'none' : 'none',
            '&:hover': { bgcolor: colors.playBtnBg, opacity: 0.92 },
            '&.Mui-disabled': { bgcolor: isOwn ? 'rgba(255,255,255,0.5)' : '#e8eaed', color: '#9aa0a6' },
          }}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 20 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 22, ml: 0.25 }} />
          )}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 26 }}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: colors.progressDot,
                flexShrink: 0,
                ml: `${Math.min(100, progress * 100) * 0.15}%`,
                transition: 'margin-left 0.08s linear',
                display: progress > 0 ? 'block' : 'none',
              }}
            />
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {waveform.map((amp, i) => {
                const played = i <= progressIndex;
                const isDot = amp < 0.22;
                if (isDot && !played) {
                  return (
                    <Box
                      key={i}
                      sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: colors.barUnplayed, flexShrink: 0 }}
                    />
                  );
                }
                return (
                  <Box
                    key={i}
                    sx={{
                      width: 2.5,
                      borderRadius: 1,
                      height: Math.max(3, amp * 22),
                      bgcolor: played ? colors.barPlayed : colors.barUnplayed,
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.68rem', color: colors.duration, mt: 0.25, ml: 0.25 }}>
            {formatAudioDuration(displayMs)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
